import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LogOut, Mail, ShieldCheck } from 'lucide-react';
import { supabase } from './supabase';
import { Button } from './ui/arc/button';

// Segundo paso del login (15/10/2026): después de usuario y contraseña, un código de 6 dígitos que
// llega por mail (edge function two-factor → Resend, desde info@krilic.com.ar). La sesión queda
// verificada hasta cerrar sesión. Ver App.tsx (twoFA) y migr 108.
export function TwoFactor({ emailHint, onVerified }: { emailHint: string; onVerified: () => void }) {
    const [code, setCode] = useState('');
    const [sending, setSending] = useState(false);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [cooldown, setCooldown] = useState(0);
    const sent = useRef(false);
    const input = useRef<HTMLInputElement>(null);

    const send = useCallback(async () => {
        setSending(true); setError(''); setInfo('');
        const { data, error: e } = await supabase.functions.invoke('two-factor', { body: { action: 'send' } });
        setSending(false);
        if (e || !data?.ok) { setError(data?.error || 'No se pudo enviar el código. Probá de nuevo.'); return; }
        if (data.verified) { onVerified(); return; }
        setInfo(data.throttled ? 'Ya te mandamos un código hace menos de un minuto: revisá tu mail.' : `Te mandamos un código a ${data.email_hint || emailHint}.`);
        setCooldown(60);
        input.current?.focus();
    }, [emailHint, onVerified]);

    useEffect(() => { if (!sent.current) { sent.current = true; send(); } }, [send]);
    useEffect(() => { if (cooldown <= 0) return; const t = setTimeout(() => setCooldown(c => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);

    const verify = async (c = code) => {
        if (c.length !== 6) return;
        setChecking(true); setError('');
        const { data, error: e } = await supabase.functions.invoke('two-factor', { body: { action: 'verify', code: c } });
        setChecking(false);
        if (e || !data?.ok) { setError(data?.error || 'No se pudo verificar.'); setCode(''); input.current?.focus(); return; }
        onVerified();
    };

    return (
        <div className="tw grid min-h-screen place-items-center bg-[var(--background)] px-4">
            <div className="w-full max-w-[380px] rounded-[var(--radius-surface)] border border-line bg-card p-6 shadow-xs">
                <img src="/logo.png" alt="Krilic" style={{ height: 24 }} />
                <div className="mt-5 flex items-center gap-2 text-ink">
                    <ShieldCheck size={18} /> <h1 className="m-0 text-[length:var(--text-lg)] font-semibold">Verificá que sos vos</h1>
                </div>
                <p className="m-0 mt-2 text-[length:var(--text-sm)] text-soft">
                    {info || `Te estamos mandando un código de 6 dígitos a ${emailHint}…`}
                </p>
                <form className="mt-5 grid gap-3" onSubmit={e => { e.preventDefault(); verify(); }}>
                    <input ref={input} autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
                        aria-label="Código de verificación" value={code}
                        onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setCode(v); setError(''); if (v.length === 6) verify(v); }}
                        className="h-12 w-full rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card text-center text-[length:var(--text-2xl)] font-semibold tracking-[0.5em] tabular-nums text-ink focus:border-[var(--foreground)] focus:outline-none" />
                    {error && <p className="m-0 text-[length:var(--text-sm)] text-[var(--danger)]">{error}</p>}
                    <Button type="submit" loading={checking} disabled={code.length !== 6}>Entrar</Button>
                </form>
                <div className="mt-4 flex items-center justify-between gap-2">
                    <Button variant="ghost" size="sm" onClick={send} disabled={sending || cooldown > 0} loading={sending}>
                        <Mail size={14} /> {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar código'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => supabase.auth.signOut({ scope: 'local' })}><LogOut size={14} /> Salir</Button>
                </div>
            </div>
        </div>
    );
}
